import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Search } from 'lucide-react';
import { Service, ServiceCategory, createService, createServiceCategory } from '../lib/services';
import { useEventDrivenModalPersistence } from '../utils/modalPersistence';

interface CreateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (service: Service) => void;
  businessId?: string;
  categories?: ServiceCategory[];
}

export default function CreateServiceModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  categories = []
}: CreateServiceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [newCategoryName, setNewCategoryName] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Event-driven modal persistence
  const { saveModalData, loadModalData, clearModalData } = useEventDrivenModalPersistence('create_service');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    tax_exempt: false,
    price_options: [
      { name: 'Default', price: '', duration_minutes: '' }
    ],
    add_ons: [] as Array<{
      name: string;
      description: string;
      price: string;
      duration_minutes: string;
    }>
  });

  // Helper function to save current form state with optional overrides
  const saveServiceProgress = (overrides: Record<string, any> = {}) => {
    const serviceData = {
      formData,
      selectedCategory,
      newCategoryName,
      categorySearch,
      ...overrides // Apply any immediate overrides to handle state timing issues
    };
    console.log('💾 Saving CreateServiceModal progress:', Object.keys(serviceData));
    saveModalData(serviceData);
  };

  const isFormValid = () => {
    // Check if all required fields are filled
    const hasCategory = formData.category_id || newCategoryName;
    const hasName = formData.name.trim() !== '';
    const hasDescription = formData.description.trim() !== '';
    
    // Check if all price options are valid
    const hasPriceOptions = formData.price_options.every(option => 
      option.name.trim() !== '' && 
      option.price !== '' && 
      option.duration_minutes !== ''
    );

    // Check if all add-ons are valid
    const hasValidAddOns = formData.add_ons.every(addon => 
      addon.name.trim() !== '' && 
      addon.description.trim() !== '' && 
      addon.price !== '' && 
      addon.duration_minutes !== ''
    );

    return hasCategory && hasName && hasDescription && hasPriceOptions && hasValidAddOns && !loading;
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateServiceModal progress...');
      const savedData = loadModalData();
      
      if (Object.keys(savedData).length > 0) {
        console.log('📥 Restoring saved progress:', Object.keys(savedData));
        
        if (savedData.formData) setFormData(savedData.formData);
        if (savedData.selectedCategory) setSelectedCategory(savedData.selectedCategory);
        if (savedData.newCategoryName) setNewCategoryName(savedData.newCategoryName);
        if (savedData.categorySearch) setCategorySearch(savedData.categorySearch);
      } else {
        console.log('📥 No saved CreateServiceModal data found');
      }
    }
  }, [isOpen, loadModalData]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Clear saved data when modal closes
      console.log('🗑️ Clearing CreateServiceModal localStorage on close');
      clearModalData();
      // Reset all form state
      setFormData({
        name: '',
        description: '',
        category_id: '',
        tax_exempt: false,
        price_options: [
          { name: 'Default', price: '', duration_minutes: '' }
        ],
        add_ons: []
      });
      setCategorySearch('');
      setSelectedCategory(null);
      setNewCategoryName(null);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let categoryId = formData.category_id;

      // Create new category if needed
      if (newCategoryName) {
        const newCategory = await createServiceCategory({
          business_id: businessId,
          name: newCategoryName,
          description: '',
          order_index: categories.length
        });
        categoryId = newCategory.id;
      }

      const service = await createService(
        {
          business_id: businessId,
          name: formData.name.trim(),
          description: formData.description.trim(),
          category_id: categoryId,
          color: '#000000',
          tax_exempt: formData.tax_exempt,
          tax_rate_id: null,
        },
        formData.price_options.map(option => ({
          name: option.name.trim(),
          price: parseFloat(option.price.toString()),
          duration_minutes: parseInt(option.duration_minutes.toString())
        })),
        formData.add_ons.map((addon, index) => ({
          name: addon.name.trim(),
          description: addon.description.trim(),
          price: parseFloat(addon.price.toString()),
          duration_minutes: parseInt(addon.duration_minutes.toString()),
          business_id: businessId,
          order_index: index,
          addon_service_id: 'temp_id'
        }))
      );

      console.log('✅ Service created successfully, clearing localStorage');
      clearModalData();
      onSuccess(service);
    } catch (error) {
      console.error('Error creating service:', error);
      setError('Failed to create service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addPriceOption = () => {
    const newFormData = {
      ...formData,
      price_options: [
        ...formData.price_options,
        { name: '', price: '', duration_minutes: '' }
      ]
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const removePriceOption = (index: number) => {
    const newFormData = {
      ...formData,
      price_options: formData.price_options.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const updatePriceOption = (index: number, field: string, value: any) => {
    const newFormData = {
      ...formData,
      price_options: formData.price_options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const addAddOn = () => {
    const newFormData = {
      ...formData,
      add_ons: [
        ...formData.add_ons,
        { name: '', description: '', price: '', duration_minutes: '' }
      ]
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const removeAddOn = (index: number) => {
    const newFormData = {
      ...formData,
      add_ons: formData.add_ons.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const updateAddOn = (index: number, field: string, value: string | number) => {
    const updatedAddOns = [...formData.add_ons];
    updatedAddOns[index] = {
      ...updatedAddOns[index],
      [field]: value.toString()
    };
    const newFormData = {
      ...formData,
      add_ons: updatedAddOns
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  // Add useEffect to handle clicking outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  if (!isOpen && !isAnimating) return null;

  const filteredCategories = categorySearch
    ? categories.filter(category =>
        category.name.toLowerCase().includes(categorySearch.toLowerCase())
      )
    : [];

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Create Service</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <div className="relative" ref={dropdownRef}>
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    required
                    placeholder="Search or select a category..."
                    value={selectedCategory ? `Selected "${selectedCategory.name}"` : newCategoryName ? `Creating "${newCategoryName}"` : categorySearch}
                    onChange={(e) => {
                      setCategorySearch(e.target.value);
                      // Only open dropdown if there's input or categories exist
                      if (e.target.value.trim() !== '' || categories.length > 0) {
                        setIsDropdownOpen(true);
                      }
                    }}
                    onClick={() => {
                      // Only open dropdown if not already selected category or creating new one
                      if (!selectedCategory && !newCategoryName && categories.length > 0) {
                        setIsDropdownOpen(!isDropdownOpen);
                      }
                    }}
                    className="w-full pl-8 pr-10 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                  {/* Dropdown toggle button */}
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      if (!selectedCategory && !newCategoryName) {
                        setIsDropdownOpen(!isDropdownOpen);
                      }
                    }}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 hover:text-gray-700"
                  >
                    <svg 
                      className={`h-5 w-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu */}
                  {(!selectedCategory && !newCategoryName) && isDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white rounded-md shadow-lg border border-gray-200 z-10">
                      {categories.length > 0 ? (
                        <div>
                          {/* Always show all categories or filtered results */}
                          {(categorySearch.trim() === '' ? categories : filteredCategories).map(category => (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => {
                                const newFormData = { ...formData, category_id: category.id };
                                setSelectedCategory(category);
                                setFormData(newFormData);
                                setCategorySearch('');
                                setIsDropdownOpen(false);
                                saveServiceProgress({ 
                                  selectedCategory: category, 
                                  formData: newFormData,
                                  categorySearch: ''
                                });
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                            >
                              {category.name}
                            </button>
                          ))}
                          
                          {/* Show create option if search doesn't match any categories */}
                          {categorySearch.trim() !== '' && filteredCategories.length === 0 && (
                            <div className="text-center py-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setNewCategoryName(categorySearch);
                                  setCategorySearch('');
                                  setIsDropdownOpen(false);
                                  saveServiceProgress({ 
                                    newCategoryName: categorySearch,
                                    categorySearch: ''
                                  });
                                }}
                                className="mt-1 text-sm text-blue-600 hover:text-blue-700"
                              >
                                Create "{categorySearch}"
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <p className="text-sm text-gray-500">No categories available</p>
                          {categorySearch.trim() !== '' && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewCategoryName(categorySearch);
                                setCategorySearch('');
                                setIsDropdownOpen(false);
                                saveServiceProgress({ 
                                  newCategoryName: categorySearch,
                                  categorySearch: ''
                                });
                              }}
                              className="mt-1 text-sm text-blue-600 hover:text-blue-700"
                            >
                              Create "{categorySearch}"
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Selected Category or Creating New Category Info */}
                {(selectedCategory || newCategoryName) && (
                  <div className="mt-2 flex items-center">
                    <span className="text-sm text-gray-500">
                      {selectedCategory ? `Using category: ${selectedCategory.name}` : `Creating new category: ${newCategoryName}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newFormData = { ...formData, category_id: '' };
                        setSelectedCategory(null);
                        setNewCategoryName(null);
                        setFormData(newFormData);
                        saveServiceProgress({ 
                          selectedCategory: null,
                          newCategoryName: null,
                          formData: newFormData
                        });
                      }}
                      className="ml-2 text-xs text-red-600 hover:text-red-700"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Basic Information */}
              <div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => {
                        const newFormData = { ...formData, name: e.target.value };
                        setFormData(newFormData);
                        saveServiceProgress({ formData: newFormData });
                      }}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                      placeholder="Enter service name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => {
                        const newFormData = { ...formData, description: e.target.value };
                        setFormData(newFormData);
                        saveServiceProgress({ formData: newFormData });
                      }}
                      rows={4}
                      className="w-full rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                      placeholder="Enter service description"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.tax_exempt}
                        onChange={(e) => {
                          const newFormData = { ...formData, tax_exempt: e.target.checked };
                          setFormData(newFormData);
                          saveServiceProgress({ formData: newFormData });
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700">Tax Exempt</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Price Options */}
              <div>
                <div className="space-y-4">
                  {formData.price_options.map((option, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={option.name}
                            onChange={(e) => updatePriceOption(index, 'name', e.target.value)}
                            className="w-full h-9 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                            placeholder="e.g., Basic, Premium"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Price *
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              required
                              step="0.01"
                              value={option.price}
                              onChange={(e) => updatePriceOption(index, 'price', e.target.value)}
                              className="w-full pl-7 pr-4 h-9 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Duration (hours) *
                          </label>
                          <input
                            type="number"
                            required
                            min="0"
                            step="0.5"
                            value={option.duration_minutes ? (parseInt(option.duration_minutes) / 60).toString() : ''}
                            onChange={(e) => updatePriceOption(index, 'duration_minutes', Math.round(parseFloat(e.target.value || '0') * 60).toString())}
                            className="w-full h-9 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                          />
                        </div>
                      </div>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removePriceOption(index)}
                          className="mt-2 text-xs text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addPriceOption}
                  className="mt-4 inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Option
                </button>
              </div>

              {/* Add-ons Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-900">Add-ons</h3>
                  <button
                    type="button"
                    onClick={addAddOn}
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Add-on
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.add_ons.map((addon, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={addon.name}
                            onChange={(e) => updateAddOn(index, 'name', e.target.value)}
                            className="w-full h-9 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                            placeholder="e.g., Premium Wax, Extra Coating"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={addon.description}
                            onChange={(e) => updateAddOn(index, 'description', e.target.value)}
                            rows={2}
                            className="w-full rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                            placeholder="Describe what this add-on includes"
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Price
                          </label>
                          <div className="mt-1">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={addon.price || ''}
                              onChange={(e) => updateAddOn(index, 'price', e.target.value)}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Duration (hours)
                          </label>
                          <div className="mt-1">
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={addon.duration_minutes ? (parseInt(addon.duration_minutes) / 60).toString() : ''}
                              onChange={(e) => updateAddOn(index, 'duration_minutes', Math.round(parseFloat(e.target.value || '0') * 60).toString())}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeAddOn(index)}
                        className="mt-4 text-xs text-red-600 hover:text-red-700"
                      >
                        Remove Add-on
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={!isFormValid()}
                className={`w-full px-4 py-2 text-sm font-medium text-white ${
                  isFormValid() ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-300 cursor-not-allowed'
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Creating...' : 'Create Service'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}